import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import './ZoomableImage.css';
import { cc } from '../../modules/styles';

// Bound zoom and provide a predictable scale for click-to-toggle.
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const TOGGLE_SCALE = 2;

function ZoomableImage({
  src,
  alt,
  className,
  width,
  height,
  loading,
  allowNativePan = false,
  onZoomChange,
}) {
  // Track image geometry, active pointer gestures, and the current GPU transform.
  const imageRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const suppressClickRef = useRef(false);
  // State drives the rendered transform; the ref lets event handlers read it synchronously.
  const [transform, setTransform] = useState({ scale: MIN_SCALE, x: 0, y: 0 });
  const transformRef = useRef(transform);
  const wasZoomedRef = useRef(transform.scale > MIN_SCALE);
  const [animateTransform, setAnimateTransform] = useState(false);

  // Keep the ref current so successive pointer events use the latest transform.
  function updateTransform(nextTransform, animate = false) {
    // Update the ref immediately because React state updates may not render before the next event.
    transformRef.current = nextTransform;
    setTransform(nextTransform);
    setAnimateTransform(animate);
  }

  // Limit panning so the scaled image cannot be moved beyond the viewport.
  function clampTranslation(scale, x, y) {
    const image = imageRef.current;
    if (!image) {
      return { x: 0, y: 0 };
    }

    const maxX = Math.max(
      0,
      (image.clientWidth * scale - window.innerWidth) / 2
    );
    const maxY = Math.max(
      0,
      (image.clientHeight * scale - window.innerHeight) / 2
    );

    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }

  // Zoom around an anchor point, clamping both scale and resulting translation.
  function zoomTo(scale, anchorX = 0, anchorY = 0, animate = false) {
    const current = transformRef.current;
    const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

    if (nextScale === MIN_SCALE) {
      updateTransform({ scale: MIN_SCALE, x: 0, y: 0 }, animate);
      return;
    }

    const ratio = nextScale / current.scale;
    const { x, y } = clampTranslation(
      nextScale,
      anchorX - (anchorX - current.x) * ratio,
      anchorY - (anchorY - current.y) * ratio
    );

    updateTransform({ scale: nextScale, x, y }, animate);
  }

  // Image clicks toggle zoom; suppress clicks generated after a drag or pinch.
  function handleClick(event) {
    event.stopPropagation();

    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    toggleZoom(
      event.clientX - window.innerWidth / 2,
      event.clientY - window.innerHeight / 2
    );
  }

  // Zoom toward the activation point, with keyboard activation using the center.
  function toggleZoom(anchorX = 0, anchorY = 0) {
    if (transformRef.current.scale === MIN_SCALE) {
      zoomTo(TOGGLE_SCALE, anchorX, anchorY, true);
    } else {
      updateTransform({ scale: MIN_SCALE, x: 0, y: 0 }, true);
    }
  }

  // Prevent keyboard activation from bubbling to the modal's close handler.
  function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      toggleZoom();
    }
  }

  // Zoom around the pointer location while normalizing wheel delta units.
  function handleWheel(event) {
    // Let horizontal trackpad gestures scroll the containing gallery while unzoomed.
    if (
      transformRef.current.scale === MIN_SCALE &&
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
    ) {
      return;
    }

    event.preventDefault();
    const bounds = imageRef.current.getBoundingClientRect();
    const anchorX = event.clientX - (bounds.left + bounds.width / 2);
    const anchorY = event.clientY - (bounds.top + bounds.height / 2);
    const deltaY =
      event.deltaY *
      (event.deltaMode === 1
        ? 16
        : event.deltaMode === 2
        ? window.innerHeight
        : 1);

    zoomTo(
      transformRef.current.scale * Math.exp(-deltaY * 0.002),
      anchorX,
      anchorY,
      true
    );
  }

  // Record pointers and initialize either a pan or pinch gesture.
  function handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    setAnimateTransform(false);

    if (pointersRef.current.size === 0) {
      suppressClickRef.current = false;
    }

    // Capture the pointer so drags continue even when the pointer leaves the image.
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    // Store the starting geometry needed to calculate pan offsets or pinch scale.
    const pointers = Array.from(pointersRef.current.values());
    const current = transformRef.current;

    if (pointers.length === 1) {
      gestureRef.current = {
        type: 'pan',
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
      };
    } else if (pointers.length === 2) {
      const [first, second] = pointers;
      const midpointX = (first.x + second.x) / 2;
      const midpointY = (first.y + second.y) / 2;

      gestureRef.current = {
        type: 'pinch',
        distance: Math.hypot(second.x - first.x, second.y - first.y),
        scale: current.scale,
        originX: current.x,
        originY: current.y,
        midpointX,
        midpointY,
      };
      suppressClickRef.current = true;
    }
  }

  // Pan with one pointer when zoomed, or scale and reposition around a pinch.
  function handlePointerMove(event) {
    if (!pointersRef.current.has(event.pointerId)) {
      return;
    }

    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    const pointers = Array.from(pointersRef.current.values());
    const gesture = gestureRef.current;

    if (pointers.length === 1 && gesture?.type === 'pan') {
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        suppressClickRef.current = true;
      }

      if (transformRef.current.scale > MIN_SCALE) {
        const { x, y } = clampTranslation(
          transformRef.current.scale,
          gesture.originX + deltaX,
          gesture.originY + deltaY
        );
        updateTransform({ ...transformRef.current, x, y });
      }
    } else if (pointers.length === 2 && gesture?.type === 'pinch') {
      if (gesture.distance === 0) {
        return;
      }

      // Scale from the starting finger distance while keeping the pinch midpoint anchored.
      const [first, second] = pointers;
      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      const midpointX = (first.x + second.x) / 2;
      const midpointY = (first.y + second.y) / 2;
      const nextScale = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, gesture.scale * (distance / gesture.distance))
      );
      const ratio = nextScale / gesture.scale;
      const anchorX = gesture.midpointX - window.innerWidth / 2;
      const anchorY = gesture.midpointY - window.innerHeight / 2;
      const { x, y } = clampTranslation(
        nextScale,
        midpointX - window.innerWidth / 2 - (anchorX - gesture.originX) * ratio,
        midpointY - window.innerHeight / 2 - (anchorY - gesture.originY) * ratio
      );

      updateTransform({ scale: nextScale, x, y });
    }
  }

  // End the current gesture or continue panning with the remaining pointer.
  function handlePointerUp(event) {
    pointersRef.current.delete(event.pointerId);

    const remaining = Array.from(pointersRef.current.values());
    if (remaining.length === 1) {
      gestureRef.current = {
        type: 'pan',
        startX: remaining[0].x,
        startY: remaining[0].y,
        originX: transformRef.current.x,
        originY: transformRef.current.y,
      };
    } else {
      gestureRef.current = null;
    }
  }

  // Reset gesture and zoom state whenever the displayed image changes.
  useEffect(() => {
    const initialTransform = { scale: MIN_SCALE, x: 0, y: 0 };
    transformRef.current = initialTransform;
    setTransform(initialTransform);
    pointersRef.current.clear();
    gestureRef.current = null;
    suppressClickRef.current = false;
    setAnimateTransform(false);
  }, [src]);

  // Notify only real zoom-state transitions, not the initial unzoomed render.
  useEffect(() => {
    const isZoomed = transform.scale > MIN_SCALE;
    if (isZoomed !== wasZoomedRef.current) {
      wasZoomedRef.current = isZoomed;
      onZoomChange?.(isZoomed);
    }
  }, [onZoomChange, transform.scale]);

  // React's wheel handler may be passive; attach a non-passive listener so preventDefault stops page scrolling.
  useEffect(() => {
    const image = imageRef.current;
    if (!image) {
      return undefined;
    }

    const onWheel = event => handleWheel(event);

    // Remove the native listener when the image changes or the modal unmounts.
    image.addEventListener('wheel', onWheel, { passive: false });
    return () => image.removeEventListener('wheel', onWheel);
  }, [src]);

  return (
    <img
      ref={imageRef}
      className={cc(
        'ZoomableImage',
        animateTransform ? 'ZoomableImage--animated' : '',
        className
      )}
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      role="button"
      tabIndex={0}
      draggable={false}
      style={{
        // Permit native horizontal touch scrolling only for opted-in, unzoomed images.
        touchAction:
          allowNativePan && transform.scale === MIN_SCALE ? 'pan-x' : 'none',
        // Apply zoom and pan as a composited 3D transform.
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale3d(${transform.scale}, ${transform.scale}, 1)`,
        cursor: transform.scale === MIN_SCALE ? 'zoom-in' : 'zoom-out',
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    />
  );
}

ZoomableImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  allowNativePan: PropTypes.bool,
  onZoomChange: PropTypes.func,
};

export default ZoomableImage;
